import $ from 'jquery'
import _ from 'lodash'
import Backbone from 'backbone'
import globals from 'scoreboard/components/globals'
import {
	getAndClearLastNavigatedTo
} from 'scoreboard/global-search/global-search'
import appDisplay from 'scoreboard/components/app-display'
import conductor from 'scoreboard/components/conductor'
import spectator from './spectator'
import errorHandler from './error-handler'
import calendars from './calendars'
import noCalendars from './no-calendars.hbs'
import EmptyState from './empty-state'
import noPermission from './no-permission.hbs'
import i18n from './i18n'
import projects from 'scoreboard/projects/projects'
import briefer from 'scoreboard/briefings/briefer'
import briefingsLayoutManager from 'scoreboard/briefings/briefings-layout-manager'
import license from 'scoreboard/components/license'
import licenseRestrictionView from 'scoreboard/components/license-restriction-view'
import layoutManager from 'scoreboard/components/layout-manager'
import metadataObjects from './metadata-objects'

var BaseRouter = Backbone.Router.extend({

	/**
	 * Centralizes navigation concerns when loading a view with a model
	 * (e.g. overview, edit, etc)
	 *
	 * Waits for the model and the nav request's to be fulfilled and sets the
	 * editability based on model.isEditable()
	 *
	 * @param {NavOptions} options
	 *
	 * @param {Boolean} options.edit pass true if this is an edit view, will force
	 * the edit view state to be applied.
	 *
	 * @param {Promise<Backbone.Model>} modelPromise The model that fulfills the promise
	 * must implement `isEditable`
	 *
	 * @returns {Promise<Backbone.View>}
	 */
	navForModel(options, modelPromise) {
		const navPromise = this.nav(_.extend({
			// We know if we're not explicitly in edit mode we should leave edit mode
			toggleEdit: options.edit === true
		}, options))
		Promise.all([navPromise, modelPromise]).then(([navView, model]) => {
			_.invoke(navView, 'toggleEditability', model.isEditable())
		}).catch(response => {
			// The display promise will reject non-success; (see base-router#load)
			// The catch is so JS errors don't show up that it's unhandled
			console.debug('modelPromise rejected:', response)
		})
		return navPromise
	},

	/**
	 * To be called when a route is triggered to create a new model.
	 * (e.g. Dashboard, Folder, Form, Form Page, etc.)
	 *
	 * This will update both the nav and the display at the same time.
	 *
	 * The form and model are required to create the transient node
	 *
	 * @param {NavOptions} options (this function extends from NavOptions)
	 *
	 * @param {Number} options.relativeId Where the folder will be attached in the tree
	 *
	 * @param {Backbone.View|Promise<Backbone.View>} options.form The display pane form view
	 *
	 * @param {Function} options.viewCreator If options.form is a promise, viewCreator
	 * must return the view for the fulfilled promise value
	 *
	 * @param {Backbone.Model} options.model
	 *
	 * @param {Boolean} [options.delayViewRemove=false] see appDisplay.display
	 *
	 * @returns {Promise} A promise fulfilled with the Nav's view should the caller
	 * have need to do anything further with it.
	 */
	navForNewModel(options) {
		const {relativeId, orgId, form, model,
			delayViewRemove, viewCreator} = options

		const navPromise = this.nav({
			id: relativeId,
			orgId,
			toggleEditViewState: true
		})
		// Datasets have complex permissions logic and the appDisplay is handled there
		if (form && model) {
			const viewPromise = appDisplay.display(form, viewCreator, delayViewRemove)
			navPromise.then(navView => {
				Promise.resolve(viewPromise || form).then(editView => {
					this.addTransient(editView, relativeId, model)
				})
			})
		}
		return navPromise
	},

	/**
	 * @typedef NavOptions
	 *
	 * @property {Number} [id] A model id
	 *
	 * @property {Number} [orgId] An org id
	 *
	 * @property {Boolean} [toggleEdit]
	 * 	If true navView.toggleEdit() will be invoked, and also navView.toggleEditViewState()
	 *
	 * @property {Boolean} [toggleEditViewState]
	 * 	If true navView.toggleEditViewState() will be invoked, but not navView.toggleEdit()
	 *
	 * @property {Boolean} [keepTransient=false]
	 * 	If true, removeTransient will not be invoked on the nav
	 */

	/**
	 * This should only be directly called if the promise for the model isn't
	 * available.  Otherwise, use `navForModel` or `navForNewModel`
	 *
	 * Returns a promise that is fulfilled with the view rendered in the nav pane.
	 *
	 * @param {NavOptions} options
	 */
	nav: function(options = {}) {
		//fullscreen views may not render the nav pane
		if (this.Nav) {
			options.requireSuccess = appDisplay.isDrilldown()
			let navView = appDisplay.navView()
			const thisSection = this.Nav.prototype.section
			const spectatedView = spectator.getCurrent()
			const spectatingSameSection = (spectatedView && spectator.spectates(thisSection) &&
				thisSection === spectatedView.section)
			if (_.isNil(navView) || _.isNil(spectatedView) || spectatingSameSection) {
					const promise = appDisplay.nav(this.Nav, options)
					return promise.then(v => {
						navView = v

						let { toggleEdit, toggleEditViewState,
							keepTransient = false} = options

						if (!keepTransient) {
							_.invoke(navView, 'removeTransient')
						}

						if (!_.isNil(toggleEdit)) {
							_.invoke(navView, 'toggleEdit', toggleEdit)
						} else if (!_.isNil(toggleEditViewState)) {
							_.invoke(navView, 'toggleEditViewState', toggleEditViewState)
						}

						_.invoke(navView, 'show', options)

						return navView
					})
			}
			return Promise.resolve(navView)
		}
	},

	// add a placeholder (aka "transient") node to the tree where a new node
	// is about to be created.  This must be called AFTER the nav has been
	// created
	addTransient: function(editView, relativeId, attributes) {
		attributes = attributes.attributes || attributes
		var nav = appDisplay.navView()
		nav.addTransient(relativeId, attributes)

		editView.on('change', nav.changeTransient.bind(nav))
		editView.on('save', nav.saveTransient.bind(nav, relativeId))
	},

	/**
	 * load a domain object, if necessary
	 * @param id {Number|Object} A domain object id or a hash of properties if
	 * the model needs more than the id.
	 * @param {Object} [data] passed to the 'data' property of fetch()
	 * @param {Function} [next] (model, response, options) => {...} where response
	 * is the JSON payload, and options.xhr contains the XHR Object (useful for getting headers)
	 *
	 * @returns {JQueryPromise}
	 */
	load: function(id, data, next, error) {
		var modelProps = id instanceof Object ? id : { id: id }
		var deferred = new $.Deferred()

		_.invoke(this.currentFetch, 'abort')
		this.currentFetch = new this.Model(modelProps).fetch({
			data,
 			success: function(model, resp, options) {
				this.applyModel(model)
				next && next(model)
				deferred.resolve(model, resp, options)
				delete this.currentFetch
			}.bind(this),
			error: (model, resp, options) => {
				if (error) {
					error(model, resp, options)
				} else {
					errorHandler(resp)
				}
				deferred.reject(resp)
				delete this.currentFetch
			}
		})
		return deferred.promise()
	},

	/**
	 * Called by `load` after a model is successfully returned. Subclasses that
	 * don't call `load` but make their own request for the model must call this
	 * function themselves.
	 *
	 * @param model
	 */
	applyModel: function (model) {
		const {projectId, organizationId} = model.attributes
		spectator.merge(model)
		Backbone.trigger('apply:domain-object', model)
		let section = conductor.section()
		if (projectId && organizationId
			&& this.shouldUpdateGlobalOrg(model, section,
				getAndClearLastNavigatedTo())) {

			// When we're in the middle of a paused briefing and we're loading a briefing,
			// from the briefings section, stick with the org which was navigated to
			// in order to load this model. There's no need to update anything. #35013
			if (section === 'briefings' && briefingsLayoutManager.isPaused()) {
				return
			}

			// If we're in the briefings section, the global orgId/projectId should
			// be governed by the currently selected briefing, not the slide #33128
			// If the current briefing id does not match the conductor id, that means
			// the display pane has not yet been updated so do not change the
			// org/proj in that case #35020.
			let briefing = _.get(briefer.slideControls, 'model.attributes')
			if (section === 'briefings' && briefing && conductor.id() == briefing.id) {
				globals.orgId = briefing ? briefing.organizationId : organizationId
				projects.id = briefing ? briefing.projectId : projectId
			} else {
				globals.orgId = organizationId
				projects.id = projectId
			}
		}
	},

	shouldUpdateGlobalOrg(model, section, lastSearch) {
		let updateOrg = true
		// #23518 don't look at spectator.getCurrent() because it
		// may not be set when rapidly switching bookmarks or slides
		if (section === 'bookmarks') {
			updateOrg = false
		} else if (lastSearch &&
			lastSearch.id === model.id &&
			lastSearch.section === section) {
			// #25830 Always apply the orgId when navigating from global search
			updateOrg = true
		}
		return updateOrg
	},

	/**
	 * for sections that require a calendar (scorecards, dashboards), handles
	 * displaying an error message if a calendar doesn't exist. in that case,
	 * returns true so you can invoke this like
	 *
	 * <pre>
	 * if (this.handleNoCalendars()) {
	 *     return
	 * }
	 * </pre>
	 *
	 * @param clearNav
	 *            {boolean} whether the nav pane should be cleared. defaults to
	 *            true
	 * @param heading
	 *            {string} the heading which should display at the top of the mobile header
	 *            of the empty state
	 *
	 * These parameters are optional
	 */
	handleNoCalendars: function({ clearNav, heading } = {}) {
		if (!_.isEmpty(calendars.calendars)) {
			return
		}

		if (clearNav || _.isUndefined(clearNav)) {
			appDisplay.nav('')
		}
		appDisplay.display(new EmptyState({
			body: noCalendars(),
			heading
		}).render().el)
		return true
	},

	execute: function (callback, args) {
		if (globals.loggedIn) {
			Backbone.history.trigger('before:route')
			return callback && callback.apply(this, args)
		} else {
			conductor.login()
		}
	},

	overviewWithModel: function(model) {
		var m = metadataObjects[this.section]
		var View = m.overview(model)
		return new View({ model: model, tabs: m.tabs })
	},

	editWithModel: function(model) {
		var m = metadataObjects[this.section]
		var View = m.edit(model)
		return new View({ model: model, tabs: m.tabs })
	},

	/**
	 * #16297 Ensures that the org id is resolved for situations where the org id cannot be obtained because it was not
	 * passed in and it has not yet been set in the global namespace (e.g. page refreshes, typing the url directly to
	 * an object)
	 *
	 * #38140 #38166 this previously changed the relative id in certain situations
	 * on SIPR, but due to side effects, we've simplified the logic here to
	 * gardener#getAddPosition
	 *
	 * @param {Integer|String} domainId the id of a domain object
	 * @param {Integer|String} [orgId=globals.orgId] If there is no orgId the
	 * model for `parentId` will be loaded and it's org will be assigned to `globals.orgId`
	 *
	 * callers should use $.when so they handle either the deferred or relative id that can be returned
	 */
	_resolveOrg (domainId, orgId) {
		orgId = orgId || globals.orgId

		if (!orgId) {
			// issue #16297 ensure we have an orgId
			// handle when the org id is null (like a page refresh)
			return this.load(domainId).then(model => {
				globals.orgId = model.get('organizationId')
			})
		}
		return domainId
	},

	_notEditableBody (model) {
		let body = noPermission()
		if (!_.isNil(model.get('syncId'))) {
			body = noPermission({message: i18n.get('common.replication.editingReplicated.sipr'),
				hideContactAdminMessage: true})
		}
		return body
	},

	/**
	 * Records the currently displayed View in the briefer (used for exports)
	 */
	setBrieferView: function (view) {
		briefer.setCurrentView(view)
	},

	/**
	 * For sections that require a license (datasets, forms), checks if the
	 * feature is enabled by license and displays a message advertising the
	 * feature if it is not yet enabled. #54374
	 * @param section
	 * @returns {boolean}
	 */
	handleLicenseRestriction: function ({section} = {}) {
		// if the section is not restricted, return false
		if (license[section]) {
			return false
		}
		const spectating = spectator.spectates(conductor.section())
		// this clears the nav pane if not spectating (bookmarks, briefings)
		if (!spectating) {
			appDisplay.nav('')
			if (!layoutManager.isNavCollapsed()) {
				layoutManager.toggleNavCollapsed(true, true)
			}
		}
		let options
		if (section === 'datasets') {
			options = {
				heading: i18n.get('datasets.datasets'),
				body: licenseRestrictionView.datasetsRestriction().render().el
			}
		} else if (section === 'forms') {
			options = {
				heading: i18n.get('forms.forms'),
				body: licenseRestrictionView.formsRestriction().render().el
			}
		}
		appDisplay.display(new EmptyState(options).render().el)
		return true
	},
})

export default BaseRouter

BaseRouter.__filename__ = 'components/base-router.js'
